import clsx from 'clsx';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { useId } from 'react';

import { HelpLink } from './HelpLink.tsx';

/**
 * The small set of shared UI atoms.
 *
 * Kept deliberately short. `docs/comparison-emrx.md` §2 is about declared extension points nobody
 * consumes; the same reasoning applies to a component library written ahead of the screens that
 * need it. Everything here is used by at least two callers in this workspace.
 *
 * Every atom carries its own accessibility behaviour rather than asking each caller to remember
 * it — `role="alert"` on the error text, `aria-busy` on a pending button, a real `<label for>`
 * bound to a generated id, so a caller gets correct accessible markup by construction rather than
 * having to remember to add it.
 */

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: 'primary' | 'secondary';
  /** Disables the control and announces it as busy. Use for an in-flight request. */
  readonly pending?: boolean;
}

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 rounded-control px-3 py-2 text-sm font-medium ' +
  'transition-colors disabled:cursor-not-allowed disabled:opacity-60';

export function Button({
  variant = 'primary',
  pending = false,
  disabled,
  className,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      // Explicit, because an omitted `type` inside a form defaults to `submit` and turns any
      // incidental button into an accidental form submission.
      type={type}
      disabled={disabled === true || pending}
      aria-busy={pending || undefined}
      className={clsx(
        BUTTON_BASE,
        variant === 'primary'
          ? 'bg-accent text-accent-ink hover:opacity-90'
          : 'border border-line bg-surface text-ink hover:bg-raised',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  readonly label: string;
  /** Shown under the control, and referenced by `aria-describedby`. */
  readonly hint?: string;
  /** Marks the control invalid and announces the message. */
  readonly error?: string;
}

/**
 * A labelled text input.
 *
 * The id comes from `useId`, not from a caller-supplied string. Two copies of the same form
 * rendered at two breakpoints with identical hardcoded ids would leave `<label for>` bound to
 * whichever copy the browser resolved last, so the other input has no accessible name at all — a
 * generated id cannot collide even if that mistake is made.
 */
export function Field({ label, hint, error, className, ...rest }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint !== undefined ? hintId : null, error !== undefined ? errorId : null]
      .filter((value): value is string => value !== null)
      .join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <input
        id={id}
        aria-invalid={error !== undefined}
        aria-describedby={describedBy}
        className={clsx(
          'w-full rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink',
          // 16px on a phone: anything smaller makes iOS Safari zoom on focus, which then leaves the
          // rest of the form off-screen. `text-base` on mobile, tightened on desktop only.
          'sm:text-sm',
          className,
        )}
        {...rest}
      />
      {hint !== undefined ? (
        <p id={hintId} className="m-0 text-sm text-ink-muted">
          {hint}
        </p>
      ) : null}
      {error !== undefined ? <ErrorText id={errorId} message={error} /> : null}
    </div>
  );
}

export interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string;
}

/** A labelled multi-line input. `Field`'s shape, for content too long for one line — a note's body. */
export function TextArea({ label, hint, error, className, ...rest }: TextAreaProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint !== undefined ? hintId : null, error !== undefined ? errorId : null]
      .filter((value): value is string => value !== null)
      .join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <textarea
        id={id}
        aria-invalid={error !== undefined}
        aria-describedby={describedBy}
        className={clsx(
          'w-full rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink',
          'sm:text-sm',
          className,
        )}
        {...rest}
      />
      {hint !== undefined ? (
        <p id={hintId} className="m-0 text-sm text-ink-muted">
          {hint}
        </p>
      ) : null}
      {error !== undefined ? <ErrorText id={errorId} message={error} /> : null}
    </div>
  );
}

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  readonly label: string;
  readonly hint?: string;
}

/** A labelled `<select>`, `Field`'s shape for a closed choice rather than free text. */
export function SelectField({ label, hint, className, children, ...rest }: SelectFieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <select
        id={id}
        aria-describedby={hint !== undefined ? hintId : undefined}
        className={clsx(
          'w-full rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink',
          'sm:text-sm',
          className,
        )}
        {...rest}
      >
        {children}
      </select>
      {hint !== undefined ? (
        <p id={hintId} className="m-0 text-sm text-ink-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * An error message that announces itself.
 *
 * `role="alert"` lives here rather than at each call site so a screen reader reads the message the
 * moment it appears, without every caller having to know that.
 */
export function ErrorText({ message, id }: { readonly message: string; readonly id?: string }) {
  return (
    <p id={id} role="alert" className="m-0 text-sm font-medium text-danger">
      {message}
    </p>
  );
}

export interface TextAreaFieldProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'id'
> {
  readonly label: string;
  /** Shown under the control, and referenced by `aria-describedby`. */
  readonly hint?: string;
  /** Marks the control invalid and announces the message. */
  readonly error?: string;
}

/**
 * A labelled multi-line text control, for the one shape `Field` cannot cover: free text long enough
 * to want line wrapping and a resizable box (an appointment's `notes`, up to
 * `MAX_APPOINTMENT_NOTES_LENGTH`). Same id/hint/error wiring as `Field` — see that component's own
 * header for why the id comes from `useId` rather than a caller-supplied string.
 */
export function TextAreaField({ label, hint, error, className, ...rest }: TextAreaFieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint !== undefined ? hintId : null, error !== undefined ? errorId : null]
      .filter((value): value is string => value !== null)
      .join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <textarea
        id={id}
        aria-invalid={error !== undefined}
        aria-describedby={describedBy}
        className={clsx(
          'w-full rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink',
          'sm:text-sm',
          className,
        )}
        {...rest}
      />
      {hint !== undefined ? (
        <p id={hintId} className="m-0 text-sm text-ink-muted">
          {hint}
        </p>
      ) : null}
      {error !== undefined ? <ErrorText id={errorId} message={error} /> : null}
    </div>
  );
}

export function Panel({
  title,
  children,
  className,
}: {
  /** A plain string is the ordinary case; `ReactNode` also admits e.g. a title with an inline
   * `HelpLink` beside it (`components/HelpLink.tsx`), without every caller needing its own
   * hand-built heading markup just to fit one small link next to the text. */
  readonly title?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <section
      className={clsx(
        'rounded-panel border border-line bg-surface p-4 shadow-panel sm:p-6',
        className,
      )}
    >
      {title !== undefined ? (
        <h2 className="mt-0 mb-3 flex items-center gap-2 text-lg font-semibold text-ink">
          {title}
        </h2>
      ) : null}
      {children}
    </section>
  );
}

/**
 * A page's own `<h1>`, with an optional `HelpLink` beside it. Extracted after an independent review
 * found `AccessPage.tsx` and `GatedPages.tsx`'s `GatedRoute` had each hand-built the identical
 * `<div className="flex items-center gap-2"><h1>...</h1><HelpLink .../></div>` wrapper — one real
 * pattern, not two coincidentally similar ones, since every page-level heading in this app needs the
 * same optional affordance. `helpTopicId` is optional because not every page has a help topic yet
 * (`GatedPages.tsx`'s `AnalyticsPage`, for one).
 */
export function PageHeading({
  title,
  helpTopicId,
}: {
  readonly title: string;
  // `| undefined` explicit, not just `?:` — a caller forwarding its own already-optional prop
  // (`GatedPages.tsx`'s `GatedRoute`) reads that prop as `string | undefined`, and
  // `tsconfig.base.json`'s `exactOptionalPropertyTypes` treats "omitted" and "present as
  // `undefined`" as different types unless the prop's own type says both are fine.
  readonly helpTopicId?: string | undefined;
}) {
  return (
    <div className="flex items-center gap-2">
      <h1 className="m-0 text-xl font-semibold text-ink">{title}</h1>
      {helpTopicId !== undefined ? <HelpLink topicId={helpTopicId} /> : null}
    </div>
  );
}

/**
 * A short label/value pair. Used by the overview page, the mobile card list, and the patient
 * demographics header.
 *
 * `break-words [overflow-wrap:anywhere]` on the value: `claude-kit/rules/invariants.md`'s "Table
 * columns need a deliberate strategy for long or variable-length content" is written about a table
 * cell, but the same content — an email address, a street address, an MRN — lands in a `DetailRow`
 * too (`routes/patients/PatientChartPage.tsx`'s demographics header), and a `<dd>` with no wrap
 * strategy has exactly the same silent-overflow failure mode a `<td>` does.
 */
export function DetailRow({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-row-line py-2 last:border-b-0 sm:flex-row sm:items-baseline sm:gap-3">
      <dt className="text-sm text-ink-muted sm:w-44 sm:shrink-0">{label}</dt>
      <dd className="m-0 text-sm text-ink break-words [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning';

const BADGE_TONE_CLASS: Record<BadgeTone, string> = {
  neutral: 'border-line text-ink-muted',
  accent: 'border-accent-text text-accent-text',
  success: 'border-success text-success',
  warning: 'border-warning text-warning',
};

/** A short status word in a pill — a note's or encounter's status. Never the only signal: the text
 * itself carries the meaning, and tone is a reinforcement a colour-blind reader does not depend on. */
export function Badge({
  tone = 'neutral',
  children,
}: {
  readonly tone?: BadgeTone;
  readonly children: ReactNode;
}) {
  return (
    <span
      className={clsx(
        'inline-block rounded-pill border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        BADGE_TONE_CLASS[tone],
      )}
    >
      {children}
    </span>
  );
}
